import { type FormEvent, useState } from 'react';
import { ApiError, api, type IndicatorLookup } from '../api/client';
import { Async } from '../components/Async';
import { useFetch } from '../useFetch';

/**
 * IP reputation, made visible and reversible (ADR-0026 increment B).
 *
 * On 2026-07-28 the platform scored its operator's own IP as hostile and denied him every path of
 * every site in his tenant. Nothing in the product showed the verdict — it was diagnosable only by
 * querying D1 directly. This screen exists so that never requires a database again.
 *
 * The shape is forced by ADR-0007: indicators are tokenized at ingest and no raw address is stored,
 * so a list of "blocked IPs" is impossible by design. Instead: an aggregate of what is enforced, and
 * a lookup where you supply the address you already suspect.
 */
export function ThreatIntel({ canEdit }: { canEdit: boolean }) {
  const intel = useFetch(() => api.threatIntel());
  const allowed = useFetch(() => api.allowlist());
  const [note, setNote] = useState<string | null>(null);

  return (
    <section>
      <h2>IP reputation</h2>
      <p className="muted">
        Addresses the platform has scored from your own sites' security events. Reputation is
        checked before any rule, on every path — so an entry here affects your whole site, not one
        URL.
      </p>

      <Async state={intel}>
        {(data) => (
          <>
            <div className="row">
              <span className="label">Enforced</span>
              <span>
                {data.counts.total === 0 ? (
                  <span className="muted">Nothing is being enforced against your sites.</span>
                ) : (
                  <>
                    {data.counts.total} indicator{data.counts.total === 1 ? '' : 's'}
                    {data.counts.blocking > 0 && (
                      <>
                        {' — '}
                        <strong>{data.counts.blocking} blocking</strong>
                      </>
                    )}
                  </>
                )}
              </span>
            </div>

            {data.counts.total > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Scope</th>
                    <th>Confidence</th>
                    <th>Seen on</th>
                    <th>Last seen</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data.indicators.map((i) => (
                    <tr key={i.id}>
                      <td>
                        {/* `block` reads as severity here, not as health. Someone opens this screen
                            because a site is unreachable — the row denying their traffic has to be
                            the one that stands out, so it takes the critical badge rather than the
                            green `enforce` one that means "protection on" elsewhere. */}
                        <span
                          className={`badge badge-${i.action === 'block' ? 'critical' : 'medium'}`}
                        >
                          {i.action}
                        </span>
                      </td>
                      <td>{i.scope}</td>
                      <td>{Math.round(i.confidence * 100)}%</td>
                      <td>{i.sourceSites ?? '—'} site(s)</td>
                      <td>{new Date(i.lastSeen).toLocaleString()}</td>
                      {canEdit && (
                        <td>
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => {
                              void api
                                .clearIndicator(i.id)
                                .then((r) => {
                                  setNote(r.note);
                                  intel.reload();
                                })
                                .catch(() => setNote('Could not clear that indicator.'));
                            }}
                          >
                            Clear
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Async>

      {note && <p className="restriction-note">{note}</p>}

      <LookupBox />

      <h3>Your own addresses</h3>
      <p className="muted">
        Declare an address as yours and its activity stops counting as evidence — the durable fix
        when your office or VPN gets scored. Clearing an indicator alone is temporary: if the
        evidence is still recent, the next pass derives it again.
      </p>
      <Async state={allowed}>
        {({ entries }) => (
          <>
            {entries.length === 0 ? (
              <p className="muted">No addresses declared.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Added</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id}>
                      {/* The address itself is never shown — it is stored only as a token, so the
                          operator-authored label is the only human-readable identity it has. */}
                      <td>{e.label}</td>
                      <td>{new Date(e.createdAt).toLocaleDateString()}</td>
                      {canEdit && (
                        <td>
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => {
                              void api.removeAllowlist(e.id).then(() => allowed.reload());
                            }}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {canEdit && <AllowlistForm onAdded={allowed.reload} />}
          </>
        )}
      </Async>
    </section>
  );
}

/** Ask about one address. The inverse of a blocklist, and the only query ADR-0007 permits. */
function LookupBox() {
  const [ip, setIp] = useState('');
  const [result, setResult] = useState<IndicatorLookup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ip.trim() || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.lookupIndicator(ip.trim()));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? 'That does not look like an IP address.'
          : 'Lookup failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h3>Check an address</h3>
      <p className="muted">
        Guard Dog never stores the addresses it scores, so they cannot be listed. Enter one to see
        whether it is being enforced against your sites — for example, your own IP if a site has
        stopped loading for you but works elsewhere.
      </p>
      <form className="form" onSubmit={(e) => void submit(e)}>
        <label>
          IP address
          <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="203.0.113.42" />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? 'Checking…' : 'Check'}
        </button>
      </form>
      {error && <p className="error">{error}</p>}
      {result &&
        (result.enforced ? (
          <p className="restriction-note">
            <strong>{result.ip}</strong> is being <strong>{result.action}</strong>ed
            {result.confidence !== null && ` at ${Math.round(result.confidence * 100)}% confidence`}
            . Declare it below if it is yours.
          </p>
        ) : (
          <p className="muted">
            <strong>{result.ip}</strong> is not being enforced against your sites.
          </p>
        ))}
    </>
  );
}

function AllowlistForm({ onAdded }: { onAdded: () => void }) {
  const [ip, setIp] = useState('');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const added = await api.addAllowlist(ip.trim(), label.trim());
      setNote(added.note);
      setIp('');
      setLabel('');
      onAdded();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? 'Enter a valid IP address and a label.'
          : 'Could not add that address.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={(e) => void submit(e)}>
      <label>
        IP address
        <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="203.0.113.42" />
      </label>
      <label>
        Label
        {/* Required: the address is stored only as a token, so without this nobody can tell later
            which entry is the office and which is a decommissioned VPN. */}
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Cleveland office"
        />
      </label>
      <button type="submit" disabled={busy}>
        {busy ? 'Adding…' : 'Declare as mine'}
      </button>
      {note && <p className="restriction-note">{note}</p>}
      {error && <p className="error">{error}</p>}
    </form>
  );
}
